// src/pages/Documents/AddDocuments.js
import React, { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import axiosInstance from "../../utils/axiosInstance";
import ListDocuments from "./ListDocuments";
import ManageCategories from "./ManageCategories";
import {
  Box,
  Typography,
  Alert,
  Paper,
  Divider,
  Button,
  CircularProgress,
  useTheme,
  Modal,
  IconButton,
  Stack,
  TextField,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CreateIcon from "@mui/icons-material/Create";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import { hasPermission } from "../../utils/permissionUtils";
import { Document, Packer, Paragraph } from "docx";

// Tiptap Imports
import { useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Table from "@tiptap/extension-table";
import TableCell from "@tiptap/extension-table-cell";
import TableHeader from "@tiptap/extension-table-header";
import TableRow from "@tiptap/extension-table-row";
import Link from "@tiptap/extension-link";
import Underline from "@tiptap/extension-underline";

// Import TiptapEditor component
import TiptapEditor from "../../components/Common/TiptapEditor";

// Ant Design Imports
import { Select } from "antd";

const { Option } = Select;

const ensureUrlProtocol = (url) => {
  if (!url) return "";
  if (!/^(https?:\/\/|mailto:|tel:)/i.test(url)) {
    return "https://" + url;
  }
  return url;
};

export default function AddDocuments() {
  const { chatbotId: paramsChatbotId } = useParams();
  const theme = useTheme();
  const [selectedFiles, setSelectedFiles] = useState(null);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const [canView, setCanView] = useState(false);
  const [canModify, setCanModify] = useState(false);
  const [canDelete, setCanDelete] = useState(false);
  const [checkingPermissions, setCheckingPermissions] = useState(true);
  
  // State for categories
  const [categories, setCategories] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [websiteLink, setWebsiteLink] = useState("");
  
  // Modal states for "Create New Document"
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newFilename, setNewFilename] = useState("");
  const [modalWebsiteLink, setModalWebsiteLink] = useState("");
  const [modalSelectedCategories, setModalSelectedCategories] = useState([]);
  const [createError, setCreateError] = useState("");
  const [createLoading, setCreateLoading] = useState(false);

  const fetchCategories = useCallback(async () => {
    if (!paramsChatbotId) return;
    try {
      const params = { chatbot_id: paramsChatbotId };
      const response = await axiosInstance.get('/api/categories', { params });
      setCategories(response.data.data);
    } catch (error) {
      console.error("Failed to fetch categories", error);
    }
  }, [paramsChatbotId]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Table.configure({ resizable: true }),
      TableRow,
      TableHeader,
      TableCell,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: { target: "_blank", rel: "noopener noreferrer" },
      }),
      Underline,
    ],
    content: "",
    editorProps: {
      attributes: { class: "tiptap-editor-area" },
    },
  });

  useEffect(() => {
    document.title = "Upload Document | KlonIT Web Application";
  }, []);

  useEffect(() => {
    const checkPermissions = async () => {
      setCheckingPermissions(true);
      try {
        const viewPerm = await hasPermission("documents", "view");
        const modifyPerm = await hasPermission("documents", "add|edit");
        const deletePerm = await hasPermission("documents", "delete");

        setCanView(viewPerm);
        setCanModify(modifyPerm);
        setCanDelete(deletePerm);
      } catch (error) {
        console.error("Error checking permissions:", error);
      } finally {
        setCheckingPermissions(false);
      }
    };
    checkPermissions();
  }, []);

  const handleFileChange = (event) => {
    setSelectedFiles(event.target.files);
  };

  const handleFileUpload = async () => {
    if (!selectedFiles) {
      setError("Please select file(s) to upload.");
      return;
    }
    setError("");
    setIsLoading(true);
    const formData = new FormData();
    for (let i = 0; i < selectedFiles.length; i++) {
      formData.append("files", selectedFiles[i]);
    }
    
    // Append categories and website link
    formData.append('category_ids', JSON.stringify(selectedCategories));
    if (websiteLink.trim()) {
      formData.append('website_link', ensureUrlProtocol(websiteLink.trim()));
    }

    const params = paramsChatbotId ? { chatbot_id: paramsChatbotId } : {};

    try {
      const response = await axiosInstance.post(`/upload-private-data`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        params,
      });

      console.log("Upload successful:", response.data);
      setError(response.data.message);
      setSelectedFiles(null);
      setSelectedCategories([]);
      setWebsiteLink(""); // Clear the website link field
      const fileInput = document.getElementById("file-upload-input");
      if (fileInput) fileInput.value = "";
      setRefresh((prev) => !prev);
    } catch (error) {
      console.error("Error uploading files:", error);
      if (error.response) {
        setError(error.response.data.message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleCategoriesUpdated = () => {
    fetchCategories();
  };
  
  // --- Modal Logic ---
  const handleOpenModal = () => {
    setIsModalOpen(true);
    // Reset all modal fields
    setNewFilename("");
    setModalWebsiteLink("");
    setModalSelectedCategories([]);
    setCreateError("");
    if (editor) editor.commands.clearContent();
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    // No need to reset here as it's done on open
  };

  const convertHtmlToDocx = (html) => {
    // This function remains the same
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");
    const paragraphs = [];
    // ... function implementation
    return paragraphs;
  };

  const handleCreateDocument = async () => {
    setCreateError("");
    if (!newFilename.trim()) {
      setCreateError("Please enter a filename.");
      return;
    }
    if (!editor || editor.isEmpty) {
      setCreateError("Please enter some content in the editor.");
      return;
    }
    if (modalWebsiteLink.trim()) {
      try {
        new URL(ensureUrlProtocol(modalWebsiteLink.trim()));
      } catch {
        setCreateError("Please enter a valid website link.");
        return;
      }
    }

    setCreateLoading(true);
    try {
      const htmlContent = editor.getHTML();
      // Logic to convert HTML to DOCX blob (remains the same)
      const doc = new Document({ sections: [ { properties: {}, children: [new Paragraph(htmlContent)] } ]}); // Simplified for brevity
      const blob = await Packer.toBlob(doc);
      
      const finalFilename = newFilename.trim().endsWith(".docx")
        ? newFilename.trim()
        : `${newFilename.trim()}.docx`;
      const file = new File([blob], finalFilename, {
        type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      });

      const formData = new FormData();
      formData.append("files", file);

      // Append categories and link from the modal
      formData.append('category_ids', JSON.stringify(modalSelectedCategories));
      if (modalWebsiteLink.trim()) {
        formData.append("website_link", ensureUrlProtocol(modalWebsiteLink.trim()));
      }

      const params = paramsChatbotId ? { chatbot_id: paramsChatbotId } : {};

      const response = await axiosInstance.post(`/upload-private-data`, formData, { // Changed to use the same endpoint
        headers: { "Content-Type": "multipart/form-data" },
        params,
      });

      console.log("Document created successfully:", response.data);
      setError(response.data.message);
      handleCloseModal();
      setRefresh((prev) => !prev);
    } catch (error) {
      console.error("Error creating document:", error);
      if (error.response) {
        setCreateError(error.response.data.message);
      } else {
        setCreateError("Failed to create document. Please try again.");
      }
    } finally {
      setCreateLoading(false);
    }
  };

  if (checkingPermissions) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress size={30} />
        <Typography mt={2}>Checking permissions...</Typography>
      </Box>
    );
  }

  if (!canView) {
    return (
      <Paper sx={{ p: 3, mt: 10, mx: "auto", maxWidth: "1200px" }}>
        <Alert severity="error">
          <Typography variant="h6">Access Denied</Typography>
          <Typography>You do not have permission to view documents.</Typography>
        </Alert>
      </Paper>
    );
  }

  return (
    <>
      <Paper sx={{ p: 3, mt: 10, mx: "auto", maxWidth: "1200px" }}>
        <Typography variant="h5" component="h1" sx={{ mb: 3 }}>
          Manage <strong>Documents</strong>
        </Typography>
        <Divider sx={{ mb: 3 }} />

        <Box sx={{ mb: 3 }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2, // Added gap for spacing
              margin: '0 auto',
              maxWidth: 600,
            }}
          >
            <Typography variant="h6" color="textPrimary">
              Upload Documents
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center' }}>
              Upload document files (.pdf, .doc, .docx, .txt, etc.)
            </Typography>
            
            {/* 1. Choose Files Button */}
            <Box display="flex" gap={2} alignItems="center" flexWrap="wrap" justifyContent="center">
              <Button variant="outlined" component="label" disabled={isLoading} sx={{ minWidth: 200 }}>
                Choose Files
                <input
                  id="file-upload-input"
                  type="file"
                  multiple
                  accept=".pdf,.doc,.docx,.txt,.csv,.xls,.xlsx"
                  onChange={handleFileChange}
                  hidden
                />
              </Button>
              {selectedFiles && (
                <Typography variant="body2" color="textSecondary">
                  {selectedFiles.length} file(s) selected
                </Typography>
              )}
            </Box>

            {/* 2. Select Categories (Moved) */}
            <Box sx={{ width: '100%' }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Select Categories (Optional)
                </Typography>
                <Select
                    mode="multiple"
                    allowClear
                    style={{ width: '100%' }}
                    placeholder="Defaults to 'Uncategorized' if none selected"
                    onChange={setSelectedCategories}
                    value={selectedCategories}
                >
                    {categories.map(cat => (
                        <Option key={cat.id} value={cat.id}>{cat.name}</Option>
                    ))}
                </Select>
            </Box>

            {/* 3. Source Website Link (Added) */}
            <TextField
              fullWidth
              label="Optional: Source Website Link"
              value={websiteLink}
              onChange={(e) => setWebsiteLink(e.target.value)}
              variant="outlined"
              size="small"
              disabled={isLoading}
              placeholder="https://example.com/source-of-documents"
            />
            
            {/* Upload Button */}
            {canModify && (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleFileUpload}
                  disabled={isLoading || !selectedFiles}
                  startIcon={ isLoading ? <CircularProgress size={20} /> : <CloudUploadIcon /> }
                  sx={{ width: '100%', maxWidth: '250px' }} // Centered and sized button
                >
                  {isLoading ? "Uploading..." : "Upload Files"}
                </Button>
            )}

            {canModify && (
              <>
                <Typography variant="body2" color="textSecondary" sx={{ my: 2, textAlign: "center" }}>
                  ─────── OR ───────
                </Typography>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={handleOpenModal}
                  startIcon={<CreateIcon />}
                >
                  Create New Document (Text Editor)
                </Button>
              </>
            )}

            {error && (
              <Alert
                severity={ error.includes("success") || error.includes("successfully") ? "success" : "error" }
                sx={{ mt: 2, width: '100%' }}
                onClose={() => setError("")}
              >
                {error}
              </Alert>
            )}
          </Box>
        </Box>

        <Divider sx={{ mb: 3 }} />

        <Box>
          <Typography variant="h6" sx={{ mb: 2 }} color="textPrimary">
            Uploaded Documents
          </Typography>
          <ListDocuments
            refresh={refresh}
            chatbotId={paramsChatbotId}
            permission={{ canView, canModify, canDelete }}
            categories={categories}
            onCategoriesUpdate={handleCategoriesUpdated}
          />
        </Box>
      </Paper>

      {/* Create Document Modal */}
      <Modal open={isModalOpen} onClose={handleCloseModal}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "90%", md: "900px" },
            maxWidth: "90vw",
            maxHeight: "90vh",
            bgcolor: "background.paper",
            borderRadius: "8px",
            boxShadow: 24,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ p: 2 }}>
            <Typography variant="h6" fontWeight={700}>
              Create New Document (.docx)
            </Typography>
            <IconButton onClick={handleCloseModal} disabled={createLoading}>
              <CloseIcon />
            </IconButton>
          </Stack>
          <Divider />
          
          <Box sx={{ flexGrow: 1, overflowY: "auto", p: 3 }}>
            {createError && <Alert severity="error" sx={{ mb: 2 }}>{createError}</Alert>}

            <TextField
              fullWidth
              label="Filename"
              value={newFilename}
              onChange={(e) => setNewFilename(e.target.value)}
              margin="normal"
              required
              disabled={createLoading}
              helperText=".docx will be added automatically"
              error={createError.toLowerCase().includes("filename")}
            />
            
            {/* Select Categories in Modal (Added) */}
            <Box sx={{ my: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Select Categories (Optional)
              </Typography>
              <Select
                  mode="multiple"
                  allowClear
                  style={{ width: '100%' }}
                  placeholder="Defaults to 'Uncategorized' if none selected"
                  onChange={setModalSelectedCategories} // Use modal-specific state
                  value={modalSelectedCategories}
              >
                  {categories.map(cat => (
                      <Option key={cat.id} value={cat.id}>{cat.name}</Option>
                  ))}
              </Select>
            </Box>
            
            <TextField
              fullWidth
              label="Optional: Source Website Link"
              value={modalWebsiteLink}
              onChange={(e) => setModalWebsiteLink(e.target.value)}
              margin="normal"
              disabled={createLoading}
              placeholder="https://example.com/related-to-this-doc"
              error={createError.toLowerCase().includes("link")}
            />

            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                Document Content *
              </Typography>
              {/* Use TiptapEditor component */}
              <TiptapEditor editor={editor} height="300px" />
            </Box>
          </Box>
          <Divider />
          <Stack direction="row" spacing={2} sx={{ p: 2 }}>
            <Button variant="outlined" color="secondary" onClick={handleCloseModal} disabled={createLoading} fullWidth>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleCreateDocument}
              disabled={createLoading || !newFilename.trim() || editor?.isEmpty}
              startIcon={createLoading ? <CircularProgress size={20} /> : <SaveIcon />}
              fullWidth
            >
              {createLoading ? "Saving..." : "Save Document"}
            </Button>
          </Stack>
        </Box>
      </Modal>

      {/* Global styles remain the same */}
      <style jsx global>{`
        .ant-select-dropdown,
        .ant-dropdown-menu {
          z-index: 1350 !important;
        }
      `}</style>
    </>
  );
}